import { calculateAge } from "./userUtils";

export type GenderCategory = "M" | "F" | "NB";
export type AgeBucket = 20 | 30 | 40 | 50 | 60 | 70;

export interface UserCategory {
  /** Short label, e.g. "M20", "F30", "NB40". */
  label: string;
  gender: GenderCategory;
  ageBucket: AgeBucket;
  age: number | null;
}

export type CompatibilityKind = "same" | "sapphic" | "male" | "any";

export interface CompatibilityCategory {
  /** Short label, e.g. "MIX 20-40", "SAME 30", "SAPPHIC 20-30". */
  label: string;
  kind: CompatibilityKind;
  /** Inclusive range of acceptable age buckets for tables this user fits in. */
  ageBuckets: AgeBucket[];
}

interface UserLike {
  quizAnswers?: any;
}

interface BookingLike {
  specialRequests?: string | null;
}

const AGE_BUCKETS: AgeBucket[] = [20, 30, 40, 50, 60, 70];

// Compatibility badge color: hue encodes kind, shade encodes age (lighter younger).
// Class strings must be statically present so Tailwind's JIT picks them up.
export const COMPATIBILITY_BG: Record<CompatibilityKind, Record<AgeBucket, string>> = {
  sapphic: {
    20: "bg-rose-400",
    30: "bg-rose-500",
    40: "bg-rose-600",
    50: "bg-rose-700",
    60: "bg-rose-800",
    70: "bg-rose-900",
  },
  male: {
    20: "bg-blue-400",
    30: "bg-blue-500",
    40: "bg-blue-600",
    50: "bg-blue-700",
    60: "bg-blue-800",
    70: "bg-blue-900",
  },
  any: {
    20: "bg-purple-400",
    30: "bg-purple-500",
    40: "bg-purple-600",
    50: "bg-purple-700",
    60: "bg-purple-800",
    70: "bg-purple-900",
  },
  same: {
    20: "bg-teal-400",
    30: "bg-teal-500",
    40: "bg-teal-600",
    50: "bg-teal-700",
    60: "bg-teal-800",
    70: "bg-teal-900",
  },
};

export function compatibilityBgClass(
  kind: CompatibilityKind,
  ageBuckets: readonly AgeBucket[]
): string {
  const median = ageBuckets[Math.floor(ageBuckets.length / 2)] ?? 40;
  return COMPATIBILITY_BG[kind][median];
}

function bucketForAge(age: number | null): AgeBucket {
  if (age === null || Number.isNaN(age)) return 30;
  if (age < 30) return 20;
  if (age < 40) return 30;
  if (age < 50) return 40;
  if (age < 60) return 50;
  if (age < 70) return 60;
  return 70;
}

function genderFromQuiz(genderIdentity: unknown): GenderCategory {
  const arr: string[] = Array.isArray(genderIdentity)
    ? genderIdentity.map((g) => String(g).toLowerCase())
    : typeof genderIdentity === "string"
    ? [genderIdentity.toLowerCase()]
    : [];

  if (arr.length === 0) return "NB";

  const isMale = (g: string) =>
    g === "male" || g === "man" || g === "cis_man" || g === "trans_man";
  const isFemale = (g: string) =>
    g === "female" ||
    g === "woman" ||
    g === "cis_woman" ||
    g === "trans_woman";
  const isNB = (g: string) =>
    g.includes("non_binary") ||
    g.includes("nonbinary") ||
    g === "nb" ||
    g === "genderqueer" ||
    g === "genderfluid";

  const hasMale = arr.some(isMale);
  const hasFemale = arr.some(isFemale);
  const hasNB = arr.some(isNB);

  if (hasNB) return "NB";
  if (hasMale && !hasFemale) return "M";
  if (hasFemale && !hasMale) return "F";
  return "NB";
}

function parseSpecialRequests(
  specialRequests?: string | null
): { gender?: string[]; age?: string[] } | null {
  if (!specialRequests) return null;
  try {
    return JSON.parse(specialRequests);
  } catch {
    return null;
  }
}

export function categorizeUser(
  user: UserLike,
  _booking?: BookingLike
): UserCategory {
  const quiz = user.quizAnswers || {};
  const age = calculateAge(quiz.birthday);
  const gender = genderFromQuiz(quiz.genderIdentity);
  const ageBucket = bucketForAge(age);

  return {
    label: `${gender}${ageBucket}`,
    gender,
    ageBucket,
    age,
  };
}

export function compatibilityFor(
  user: UserLike,
  booking?: BookingLike
): CompatibilityCategory {
  const base = categorizeUser(user, booking);
  const prefs = parseSpecialRequests(booking?.specialRequests);
  const genderPrefs = (prefs?.gender || []).map((g) => g.toLowerCase());
  const agePrefs = (prefs?.age || []).map((a) => a.toLowerCase());

  let kind: CompatibilityKind;
  if (genderPrefs.includes("sapphic")) {
    kind = "sapphic";
  } else if (genderPrefs.includes("male")) {
    kind = "male";
  } else if (genderPrefs.includes("same")) {
    kind = "same";
  } else {
    kind = "any";
  }

  const baseIdx = AGE_BUCKETS.indexOf(base.ageBucket);
  const wantsOlder = agePrefs.includes("older");
  const wantsYounger = agePrefs.includes("younger");
  const wantsMyAge = agePrefs.includes("my_age") || agePrefs.length === 0;

  let lo = baseIdx;
  let hi = baseIdx;
  if (wantsMyAge) {
    lo = Math.max(0, baseIdx - 1);
    hi = Math.min(AGE_BUCKETS.length - 1, baseIdx + 1);
  }
  if (wantsOlder) hi = Math.min(AGE_BUCKETS.length - 1, baseIdx + 2);
  if (wantsYounger) lo = Math.max(0, baseIdx - 2);
  if (lo > hi) [lo, hi] = [hi, lo];

  const ageBuckets = AGE_BUCKETS.slice(lo, hi + 1);

  const kindLabel =
    kind === "same"
      ? `SAME-${base.gender}`
      : kind === "sapphic"
      ? "S"
      : kind === "male"
      ? "M"
      : "A";

  const ageLabel =
    ageBuckets.length === 1
      ? `${ageBuckets[0]}`
      : `${ageBuckets[0]}-${ageBuckets[ageBuckets.length - 1]}`;

  return {
    label: `${kindLabel} ${ageLabel}`,
    kind,
    ageBuckets,
  };
}
